'use client';

import { useSearchParams } from 'next/navigation';
import React, { useState, useCallback, useMemo } from 'react';
import { HiSearch } from 'react-icons/hi';
import { MdVerified, MdClose, MdCheck } from 'react-icons/md';
import { IoDocumentTextOutline } from 'react-icons/io5';

import useApiQuery from 'lib/api/useApiQuery';
import useQueryWithPages from 'src/hooks/useQueryWithPages';

import { formatTimestamp } from '../../utils/formatTimestamp';
import { formatNumber, formatBalance, formatCompilerVersion } from '../../utils/formatNumber';
import styles from './VerifiedContractsPage.module.css';
import FilterDropdown from 'src/app/components/FilterDropdown';
import type { FilterSection } from 'src/app/components/FilterDropdown';

const LANGUAGE_OPTIONS: FilterSection = {
  title: 'Language',
  type: 'radio',
  key: 'filter',
  options: [
    { value: 'solidity', label: 'Solidity' },
    { value: 'vyper', label: 'Vyper' },
    { value: 'yul', label: 'Yul' },
    { value: 'scilla', label: 'Scilla' },
  ],
};

const VerifiedContractsPage: React.FC = () => {
  const searchParams = useSearchParams();
  const [searchQuery, setSearchQuery] = React.useState('');
  const [filterValues, setFilterValues] = useState<Record<string, Array<string>>>({ filter: [] });

  const handleFilterApply = useCallback((values: Record<string, Array<string>>) => {
    setFilterValues(values);
  }, []);

  const handleFilterReset = useCallback(() => {
    setFilterValues({ filter: [] });
  }, []);

  const contractsFilters = useMemo(() => {
    const f: Record<string, any> = { q: searchQuery || undefined };
    if (filterValues.filter?.length) f.filter = filterValues.filter[0];
    return f;
  }, [searchQuery, filterValues]);

  const contractsQuery = useQueryWithPages({
    resourceName: 'verified_contracts',
    filters: contractsFilters,
  });

  const countersQuery = useApiQuery('stats_contracts');

  const contracts = React.useMemo(() => {
    return contractsQuery.data?.items || [];
  }, [contractsQuery.data?.items]);

  return (
    <div className={ styles.container }>
      <div className={ styles.header }>
        <h1 className={ styles.title }>Verified Contracts</h1>
        <FilterDropdown
          label="Filter"
          sections={[LANGUAGE_OPTIONS]}
          values={filterValues}
          onApply={handleFilterApply}
          onReset={handleFilterReset}
        />
      </div>

      {/* Stats Cards */}
      <div className={ styles.statsGrid }>
        <div className={ styles.statCard }>
          <div className={ styles.statLabel }>Total contracts</div>
          <div className={ styles.statValue }>
            { (countersQuery.data as any)?.total_contracts ? formatNumber((countersQuery.data as any).total_contracts.value) : '—' }
          </div>
        </div>
        <div className={ styles.statCard }>
          <div className={ styles.statLabel }>Verified contracts</div>
          <div className={ styles.statValue }>
            { (countersQuery.data as any)?.total_verified_contracts ? formatNumber((countersQuery.data as any).total_verified_contracts.value) : '—' }
          </div>
        </div>
        <div className={ styles.statCard }>
          <div className={ styles.statLabel }>New contracts (24h)</div>
          <div className={ styles.statValue }>
            { (countersQuery.data as any)?.new_contracts_24h ? formatNumber((countersQuery.data as any).new_contracts_24h.value) : '—' }
          </div>
        </div>
        <div className={ styles.statCard }>
          <div className={ styles.statLabel }>New verified (24h)</div>
          <div className={ styles.statValue }>
            { (countersQuery.data as any)?.new_verified_contracts_24h ? formatNumber((countersQuery.data as any).new_verified_contracts_24h.value) : '—' }
          </div>
        </div>
      </div>

      {/* Search */}
      <div className={ styles.searchContainer }>
        <HiSearch className={ styles.searchIcon } size={ 20 }/>
        <input
          type="text"
          placeholder="Search by contract name or address"
          className={ styles.searchInput }
          value={ searchQuery }
          onChange={ (e) => setSearchQuery(e.target.value) }
        />
      </div>

      {/* Table */}
      <div className={ styles.tableContainer }>
        <table className={ styles.table }>
          <thead>
            <tr>
              <th>Contract</th>
              <th>Balance OGPU</th>
              <th>Txs</th>
              <th>Language / Compiler version</th>
              <th>Settings</th>
              <th>Verified</th>
              <th>License</th>
            </tr>
          </thead>
          <tbody>
            { contractsQuery.isLoading ? (
              Array.from({ length: 10 }).map((_, i) => (
                <tr key={ i }>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', minWidth: 0 }}>
                      <div className={ styles.skeleton } style={{ width: '1.25rem', height: '1.25rem', borderRadius: '0.25rem', flexShrink: 0 }} />
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                        <div className={ styles.skeleton } style={{ width: '7rem', height: '0.875rem', display: 'block' }} />
                        <div className={ styles.skeleton } style={{ width: '5rem', height: '0.75rem', display: 'block' }} />
                      </div>
                    </div>
                  </td>
                  <td><div className={ styles.skeleton } style={{ width: '4rem', height: '0.875rem' }} /></td>
                  <td><div className={ styles.skeleton } style={{ width: '2.5rem', height: '0.875rem' }} /></td>
                  <td><div className={ styles.skeleton } style={{ width: '6rem', height: '0.875rem' }} /></td>
                  <td><div className={ styles.skeleton } style={{ width: '3rem', height: '0.875rem' }} /></td>
                  <td><div className={ styles.skeleton } style={{ width: '4rem', height: '0.875rem' }} /></td>
                  <td><div className={ styles.skeleton } style={{ width: '3rem', height: '0.875rem' }} /></td>
                </tr>
              ))
            ) : contracts.length === 0 ? (
              <tr>
                <td colSpan={ 7 }>
                  <div className={ styles.emptyState }>
                    <MdVerified size={ 48 }/>
                    <p>No verified contracts found.</p>
                  </div>
                </td>
              </tr>
            ) : (
              contracts.map((contract: any, index: number) => (
                <tr key={ contract.address?.hash || index }>
                  <td>
                    <div className={ styles.contractInfo }>
                      <IoDocumentTextOutline size={ 20 } className={ styles.contractIcon }/>
                      <div>
                        <a href={ `/address/${ contract.address?.hash }` } className={ styles.contractName }>
                          { contract.address?.name || 'Unknown' }
                        </a>
                        <div className={ styles.contractAddress }>
                          { contract.address?.hash?.slice(0, 8) }...{ contract.address?.hash?.slice(-4) }
                        </div>
                      </div>
                    </div>
                  </td>
                  <td>{ formatBalance(contract.coin_balance) }</td>
                  <td>{ formatNumber(contract.transaction_count) }</td>
                  <td>
                    { formatCompilerVersion(contract.language, contract.compiler_version) }
                  </td>
                  <td>
                    <div className={ styles.settings }>
                      <span className={ styles.settingItem }>
                        { contract.optimization_enabled
                          ? <MdCheck size={ 16 } className={ styles.settingIconGreen }/>
                          : <MdClose size={ 16 } className={ styles.settingIconRed }/> }
                        <span className={ styles.settingLabel }>Optimization</span>
                      </span>
                      <span className={ styles.settingItem }>
                        { contract.has_constructor_args
                          ? <MdCheck size={ 16 } className={ styles.settingIconGreen }/>
                          : <MdClose size={ 16 } className={ styles.settingIconRed }/> }
                        <span className={ styles.settingLabel }>Constructor args</span>
                      </span>
                    </div>
                  </td>
                  <td>
                    <div className={ styles.verified }>
                      <MdVerified size={ 16 } className={ styles.verifiedIcon }/>
                      { formatTimestamp(contract.verified_at) || 'Verified' }
                    </div>
                  </td>
                  <td className={ styles.license }>
                    { contract.license_type && contract.license_type.toLowerCase() !== 'none' ? contract.license_type : '-' }
                  </td>
                </tr>
              ))
            ) }
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      { contracts.length > 0 && (
        <div className={ styles.pagination }>
          <button
            className={ styles.pageButton }
            disabled={ !contractsQuery.pagination.canGoBackwards }
            onClick={ contractsQuery.pagination.onPrevPageClick }
          >
            First
          </button>
          <button
            className={ styles.pageButton }
            disabled={ !contractsQuery.pagination.canGoBackwards }
            onClick={ contractsQuery.pagination.onPrevPageClick }
          >
            &lt;
          </button>
          <button className={ `${ styles.pageButton } ${ styles.pageButtonActive }` }>
            { contractsQuery.pagination.page }
          </button>
          <button
            className={ styles.pageButton }
            disabled={ !contractsQuery.pagination.hasNextPage }
            onClick={ contractsQuery.pagination.onNextPageClick }
          >
            &gt;
          </button>
        </div>
      ) }
    </div>
  );
};

export default VerifiedContractsPage;
